/**
 * Exceptions hub: attention-first triage landing page.
 *
 * Mounted by `SharedExceptionsList` for every `/inspect/{fp_code}/exceptions`
 * route. The bare `/exceptions` (and its `/all` alias) render the global
 * "All areas" view; `/exceptions/<slug>` filters to one native domain.
 *
 * Search is global (spans every area regardless of the active filter) and the
 * left rail is a filter ordered by the canonical `EXCEPTION_DOMAIN_ORDER` so it
 * matches the nav menu and the grouped result sections. Counts, attention, and
 * the worst band are all derived from the per-area payloads; the dashboard
 * fetch only supplies the "Updated" timestamp.
 */
import React, { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import {
  useNavigate,
  useSearchParams,
} from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  List,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import {
  Clear as ClearIcon,
  FactCheckOutlined as ExceptionsHeaderIcon,
  GridViewOutlined as GridViewIcon,
  LabelOutlined as AllTypesIcon,
  Search as SearchIcon,
} from '@mui/icons-material'

import { apiClient, inspectorApi, handleApiError } from '../../../services/api'
import { useDataFetching } from '../../../hooks/useDataFetching'
import { generateInspectorTitle, useInspector } from '../../../contexts/InspectorContext'
import { useAuth } from '../../../contexts/AuthContext'
import {
  EXCEPTION_DOMAIN_ORDER,
  EXCEPTION_DOMAIN_META,
  EXCEPTION_TYPE_META,
  ExceptionDomainIcon,
  domainToSlug,
  getDomainMeta,
  getDomainMetaBySlug,
  getExceptionTypeMeta,
  isDomainVisibleInMarket,
  isDomainVisibleToRole,
} from '../../SystemIcons'
import ExceptionPageHeader from '../shared/ExceptionPageHeader'
import { poolwerxColors } from '../../../theme/colors'
import {
  ExceptionType,
  ReportRow,
} from '../../SharedExceptionReportLayout'
import { RailItem, RAIL_SECTION_LABEL_SX } from '../../shared/FilterRail'
import { bandHex, type HealthBand } from '../dashboard/healthScore'
import { generateExceptionUrl } from '../../../utils/urls'
import { useInspectorRef } from '../../../hooks/useInspectorRef'
import {
  expandSearchTerm,
  haystackIncludesAnyTerm,
  type SearchAliasMap,
} from '../../../utils/exceptionSearchAliases'

/** The seven real, fetchable exception-domain slugs. */
export type FetchableAreaSlug =
  | 'sales'
  | 'stock-management'
  | 'product-setup'
  | 'product-pricing'
  | 'systems'
  | 'client'
  | 'accounting'

/** Adds the virtual "all areas" view used by the canonical `/exceptions`. */
export type ExceptionAreaSlug = FetchableAreaSlug | 'all'

interface AreaPayload {
  types: ExceptionType[]
}

const AREA_FETCH: Record<FetchableAreaSlug, (fpCode: string) => Promise<any>> = {
  'sales': (fp) => inspectorApi.getSalesExceptionTypes(fp),
  'stock-management': (fp) => inspectorApi.getStockManagementExceptionTypes(fp),
  'product-setup': (fp) => inspectorApi.getProductSetupExceptionTypes(fp),
  'product-pricing': (fp) => inspectorApi.getProductPricingExceptionTypes(fp),
  'systems': (fp) => inspectorApi.getSystemsExceptionTypes(fp),
  'client': (fp) => inspectorApi.getClientExceptionTypes(fp),
  'accounting': (fp) => inspectorApi.getAccountingExceptionTypes(fp),
}

/** Display meta for the virtual "All areas" view (no single domain / icon). */
const ALL_AREAS_META = {
  key: 'all',
  label: 'All areas',
  description: 'Search and explore exceptions across every area',
} as const

interface DashboardResponse {
  franchise_name?: string
  last_validation_run?: string
}

function formatTimestamp(iso?: string): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return `Updated ${d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}`
}

function matchesSearch(
  type: ExceptionType,
  term: string,
  aliases?: SearchAliasMap,
): boolean {
  if (!term) return true
  const haystack = [
    type.name,
    type.name_short,
    type.overview,
    type.why_flagged,
    (type.search_keywords ?? []).join(' | '),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
  return haystackIncludesAnyTerm(haystack, expandSearchTerm(term, aliases))
}

const URGENCY_RANK: Record<string, number> = {
  Critical: 0,
  Poor: 1,
  Watch: 2,
  Good: 3,
}

/** Sort rows by urgency: Critical → Poor → Watch → Good w/exceptions → 0-count. */
function urgencyRank(t: ExceptionType): number {
  if (t.count === 0) return 5
  const band = t.health_score?.band
  if (band && URGENCY_RANK[band] !== undefined) return URGENCY_RANK[band]
  return 4
}

function sortByUrgency(rows: ExceptionType[]): ExceptionType[] {
  return [...rows].sort((a, b) => {
    const dr = urgencyRank(a) - urgencyRank(b)
    if (dr !== 0) return dr
    return b.count - a.count
  })
}

/** Severity rank for the worst-band rail sort (lower = more urgent). */
const BAND_SEVERITY: Record<string, number> = { Critical: 0, Poor: 1, Watch: 2, Good: 3 }

const PRIORITY_BANDS: HealthBand[] = ['Critical', 'Poor', 'Watch']

function worstBand(types: ExceptionType[]): HealthBand | undefined {
  let worst: HealthBand | undefined
  let worstRank = Infinity
  for (const t of types) {
    const b = t.health_score?.band as HealthBand | undefined
    if (!b || BAND_SEVERITY[b] === undefined) continue
    if (BAND_SEVERITY[b] < worstRank) {
      worstRank = BAND_SEVERITY[b]
      worst = b
    }
  }
  return worst
}

const plural = (n: number, word: string): string => `${word}${n === 1 ? '' : 's'}`

/** Exception-type filter order for the strip: Data Discrepancy then Process
 * Issue (matches the legend the row markers read as). */
const TYPE_STRIP_ORDER = ['data_discrepancy', 'process_issue'] as const

/**
 * Quiet right-edge severity signal for the rail: a small colour dot for the
 * priority bands (Critical / Poor / Watch) only. Good and unscored show nothing
 * so the count stays the primary right-hand element.
 */
const BandDot: React.FC<{ band?: HealthBand }> = ({ band }) => {
  if (!band || !PRIORITY_BANDS.includes(band)) return null
  return (
    <Box
      component="span"
      sx={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        bgcolor: bandHex(band),
        flexShrink: 0,
      }}
    />
  )
}

interface ExceptionsHubProps {
  selectedSlug: ExceptionAreaSlug
}

const ExceptionsHub: React.FC<ExceptionsHubProps> = ({ selectedSlug }) => {
  // Bare code for data APIs + per-row deep-links (with tenureId); the raw
  // segment (fpRef) builds within-hub nav so it keeps the id-pinned form.
  const { fpCode: fp_code, tenureId, ref: fpRef } = useInspectorRef()
  const { franchiseData } = useInspector()
  const { user } = useAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const isAll = selectedSlug === 'all'

  // Search: the `?q=` param is the source of truth for filtering; a local
  // input keeps typing snappy and is written through (debounced) to the URL.
  const searchTerm = searchParams.get('q') ?? ''
  const term = searchTerm.trim().toLowerCase()
  const hasSearch = term.length > 0
  const [searchInput, setSearchInput] = useState(searchTerm)

  useEffect(() => {
    const id = setTimeout(() => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          const v = searchInput.trim()
          if (v) next.set('q', v)
          else next.delete('q')
          if (next.toString() === prev.toString()) return prev
          return next
        },
        { replace: true },
      )
    }, 250)
    return () => clearTimeout(id)
  }, [searchInput, setSearchParams])

  const clearSearch = () => {
    setSearchInput('')
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete('q')
        return next
      },
      { replace: true },
    )
  }

  // Exception-type filter: single-select, URL-bookmarkable via `?type=`. Only
  // honour a value the shared mapping knows about.
  const typeParam = searchParams.get('type')
  const activeType = typeParam && EXCEPTION_TYPE_META[typeParam] ? typeParam : null

  // Removing the type filter (active-chip delete / type-empty action) drops
  // only `?type`, leaving the area path + search term in place.
  const clearType = () => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (!next.has('type')) return prev
        next.delete('type')
        return next
      },
      { replace: false },
    )
  }

  const exceptionsBase = `/inspect/${fpRef}/exceptions`
  // Carry the LIVE search text (not the debounced URL value) so navigating
  // within the 250ms debounce window doesn't drop a just-typed query. The
  // active type filter rides along so area-rail navigation preserves it.
  const queryString = (extra?: { type?: string | null }) => {
    const params = new URLSearchParams()
    const q = searchInput.trim()
    if (q) params.set('q', q)
    const typeValue = extra && 'type' in extra ? extra.type : activeType
    if (typeValue) params.set('type', typeValue)
    const s = params.toString()
    return s ? `?${s}` : ''
  }
  const searchSuffix = queryString()
  // Type-rail rows are real links (right-click / open-in-new-tab friendly):
  // same area path, swapped `?type`, search preserved.
  const areaPath = isAll ? exceptionsBase : `${exceptionsBase}/${selectedSlug}`
  const typeTo = (key: string | null) => `${areaPath}${queryString({ type: key })}`
  const goToAll = () => navigate(`${exceptionsBase}${searchSuffix}`)

  const [areaPayloads, setAreaPayloads] = useState<Partial<Record<FetchableAreaSlug, AreaPayload>>>({})
  const [searchAliases, setSearchAliases] = useState<SearchAliasMap | undefined>(undefined)
  const [areasLoading, setAreasLoading] = useState(true)
  const [areasError, setAreasError] = useState<string | null>(null)

  // Areas visible to this user/market — drives the rail and which per-area
  // endpoints we fetch. Always iterated in EXCEPTION_DOMAIN_ORDER.
  const visibleAreas = useMemo(() => {
    return EXCEPTION_DOMAIN_ORDER
      .map((domain) => EXCEPTION_DOMAIN_META[domain])
      .filter((meta) =>
        isDomainVisibleInMarket(meta, franchiseData?.country) &&
        isDomainVisibleToRole(meta.key, user?.role),
      )
      .map((meta) => ({ meta, slug: domainToSlug(meta.key) as FetchableAreaSlug }))
  }, [franchiseData?.country, user?.role])

  // Dashboard fetch — supplies only the "Updated" timestamp now.
  const { data: dashboard } = useDataFetching<{ data: DashboardResponse }>({
    fetchFn: () => apiClient.get(`/inspector/dashboard/${fp_code}`),
    dependencies: [fp_code],
  })

  // Fan out the per-area endpoints in parallel. "All" mode reads every
  // payload; single-area mode reads the selected slug's.
  useEffect(() => {
    if (!fp_code) return
    let cancelled = false
    setAreasLoading(true)
    setAreasError(null)
    Promise.all(
      visibleAreas.map(({ slug }) =>
        AREA_FETCH[slug](fp_code)
          .then((res) => [slug, {
            types: res.data.exception_types as ExceptionType[],
            aliases: res.data.search_aliases as SearchAliasMap | undefined,
          }] as const)
          .catch((err) => {
            console.error(`Failed to load exceptions for ${slug}:`, err)
            return [slug, { types: [] as ExceptionType[], aliases: undefined }] as const
          }),
      ),
    )
      .then((entries) => {
        if (cancelled) return
        const payloadEntries = entries.map(([slug, p]) => [slug, { types: p.types }] as const)
        setAreaPayloads(Object.fromEntries(payloadEntries) as Partial<Record<FetchableAreaSlug, AreaPayload>>)
        // Every endpoint returns the same alias map; take the first non-empty.
        const aliases = entries.map(([, p]) => p.aliases).find(Boolean)
        if (aliases) setSearchAliases(aliases)
      })
      .catch((err) => {
        if (!cancelled) setAreasError(handleApiError(err))
      })
      .finally(() => {
        if (!cancelled) setAreasLoading(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fp_code, visibleAreas.map((a) => a.slug).join(',')])

  // Per-area stats. Worst band is computed off the UNFILTERED payloads so the
  // rail's severity dot stays stable while the user types.
  const areaStats = useMemo(() => {
    return visibleAreas.map(({ meta, slug }) => {
      const types = areaPayloads[slug]?.types ?? []
      const matched = hasSearch
        ? types.filter((t) => matchesSearch(t, term, searchAliases)).length
        : types.length
      return {
        meta,
        slug,
        total: types.length,
        matched,
        worst: worstBand(types),
      }
    })
  }, [visibleAreas, areaPayloads, hasSearch, term, searchAliases])

  // Rail order follows the canonical `EXCEPTION_DOMAIN_ORDER` so it stays
  // consistent with the nav menu and the grouped result sections. `areaStats`
  // is already built in that order (it maps `visibleAreas`), so we render it
  // directly rather than re-sorting by urgency.
  const railAreas = areaStats

  const allTypes = useMemo(
    () => visibleAreas.flatMap(({ slug }) => areaPayloads[slug]?.types ?? []),
    [visibleAreas, areaPayloads],
  )

  // Rows after area + search but BEFORE the type filter - the type strip
  // counts read from here so they reflect the current area + search context.
  const searchedRows = useMemo(() => {
    const source = isAll
      ? allTypes
      : (areaPayloads[selectedSlug as FetchableAreaSlug]?.types ?? [])
    return source.filter((t) => matchesSearch(t, term, searchAliases))
  }, [isAll, selectedSlug, allTypes, areaPayloads, term, searchAliases])

  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const key of TYPE_STRIP_ORDER) counts[key] = 0
    for (const t of searchedRows) {
      if (t.exception_type && counts[t.exception_type] !== undefined) {
        counts[t.exception_type] += 1
      }
    }
    return counts
  }, [searchedRows])

  // Visible rows feeding the results panel (+ active type filter + urgency).
  const visibleRows = useMemo(
    () =>
      sortByUrgency(
        activeType
          ? searchedRows.filter((t) => t.exception_type === activeType)
          : searchedRows,
      ),
    [searchedRows, activeType],
  )

  // Group by native data_domain, ordered by EXCEPTION_DOMAIN_ORDER.
  const groupedSections = useMemo(() => {
    const byKey = new Map<string, ExceptionType[]>()
    for (const t of visibleRows) {
      const key = getDomainMeta(t.data_domain).key
      const list = byKey.get(key) ?? []
      list.push(t)
      byKey.set(key, list)
    }
    return EXCEPTION_DOMAIN_ORDER
      .filter((d) => byKey.has(d))
      .map((d) => ({
        meta: EXCEPTION_DOMAIN_META[d],
        slug: domainToSlug(d) as FetchableAreaSlug,
        rows: byKey.get(d)!,
      }))
  }, [visibleRows])

  // All-areas rail row stats (matching-report counts only; severity via band).
  const allWorst = worstBand(allTypes)
  const allTotal = allTypes.length
  const allMatched = hasSearch
    ? allTypes.filter((t) => matchesSearch(t, term, searchAliases)).length
    : allTotal

  const selectedMeta = isAll ? ALL_AREAS_META : getDomainMetaBySlug(selectedSlug)
  const lastValidatedLabel = formatTimestamp(dashboard?.data?.last_validation_run)
  // The hub always presents as "Exception Reports" regardless of entry path.
  const pageHeading = 'Exception Reports'
  const pageTitle = generateInspectorTitle(pageHeading)

  const loading = areasLoading
  const error = areasError

  // Type rail options: All types + the two classifications, counts already
  // reflecting the selected area + search. `value: null` = the All-types row
  // (clears `?type`). Icons/colours come from EXCEPTION_TYPE_META so the rail
  // markers match the report-row gutter legend; All types uses a neutral glyph.
  const typeRailOptions: Array<{
    value: string | null
    label: string
    count: number
    color?: string
    Icon: React.ElementType
  }> = [
    { value: null, label: 'All types', count: searchedRows.length, Icon: AllTypesIcon },
    ...TYPE_STRIP_ORDER.map((key) => {
      const meta = EXCEPTION_TYPE_META[key]
      return {
        value: key as string | null,
        label: meta.label,
        count: typeCounts[key] ?? 0,
        color: meta.color,
        Icon: meta.icon ?? AllTypesIcon,
      }
    }),
  ]

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>

      <Box>
        <ExceptionPageHeader
          icon={<ExceptionsHeaderIcon sx={{ fontSize: 22, color: poolwerxColors.teal }} />}
          title={pageHeading}
          rightAdornment={
            lastValidatedLabel ? (
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
              >
                {lastValidatedLabel}
              </Typography>
            ) : undefined
          }
        />

        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

        {/* Global search - full width, white surface. The type filter now
            lives in the left rail (TYPE section). */}
        <TextField
          fullWidth
          size="small"
          placeholder="Search exception reports across all areas..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: 1.5,
              bgcolor: 'background.paper',
              '& fieldset': { borderColor: 'grey.400' },
              '&:hover fieldset': { borderColor: 'grey.500' },
              '&.Mui-focused fieldset': { borderColor: poolwerxColors.teal },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchInput ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={clearSearch} aria-label="Clear search">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
          {/* Left rail — AREA filter then TYPE filter, two labelled sections.
              Each is single-select and independent; the TYPE icons match the
              report-row gutter markers so the rail doubles as the legend. */}
          <Box
            sx={{
              width: 320,
              flexShrink: 0,
              display: { xs: 'none', md: 'block' },
              position: 'sticky',
              top: 16,
            }}
          >
            <Paper
              variant="outlined"
              sx={{ borderRadius: 2, borderColor: 'grey.200', py: 1, px: 0.75 }}
            >
              <Typography variant="overline" sx={RAIL_SECTION_LABEL_SX}>
                Area
              </Typography>
              <List disablePadding data-testid="area-rail">
                <RailItem
                  to={`${exceptionsBase}${searchSuffix}`}
                  active={isAll}
                  icon={<GridViewIcon sx={{ fontSize: 20, color: poolwerxColors.teal }} />}
                  label="All areas"
                  count={hasSearch ? allMatched : allTotal}
                  endAdornment={<BandDot band={allWorst} />}
                />
                {railAreas.map((row) => (
                  <RailItem
                    key={row.slug}
                    to={`${exceptionsBase}/${row.slug}${searchSuffix}`}
                    active={selectedSlug === row.slug}
                    icon={
                      <ExceptionDomainIcon
                        domain={row.meta.key}
                        variant="outlined"
                        size={20}
                        applyColor
                      />
                    }
                    label={row.meta.label}
                    count={hasSearch ? row.matched : row.total}
                    endAdornment={<BandDot band={row.worst} />}
                  />
                ))}
              </List>
              {allTypes.length > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="overline" sx={RAIL_SECTION_LABEL_SX}>
                    Type
                  </Typography>
                  <List disablePadding data-testid="type-rail">
                    {typeRailOptions.map(({ value, label, count, color, Icon }) => (
                      <RailItem
                        key={value ?? 'all'}
                        to={typeTo(value)}
                        active={(activeType ?? null) === value}
                        icon={<Icon sx={{ fontSize: 18, color: color ?? 'text.secondary' }} />}
                        label={label}
                        count={count}
                      />
                    ))}
                  </List>
                </>
              )}
            </Paper>
          </Box>

          {/* Right side — grouped results with per-area subheaders. */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                <CircularProgress size={32} />
              </Box>
            ) : (
              <Box data-testid="results-panel">
                {visibleRows.length === 0 ? (
                    // No rows at all (area/search) -> the filter-aware empty
                    // state; rows exist but the type filter hid them all ->
                    // a calmer "clear type" prompt with the toggle kept above
                    // it so switching type back is one click away.
                    searchedRows.length === 0 ? (
                      <EmptyResults
                        isAll={isAll}
                        areaLabel={selectedMeta.label}
                        searchTerm={searchTerm}
                        onClearSearch={clearSearch}
                        onSearchAllAreas={goToAll}
                      />
                    ) : (
                      <TypeEmpty
                        typeLabel={getExceptionTypeMeta(activeType!).label}
                        onClearType={clearType}
                      />
                    )
                  ) : (
                    groupedSections.map((section, i) => (
                      <AreaSection
                        key={section.meta.key}
                        meta={section.meta}
                        slug={section.slug}
                        rows={section.rows}
                        fpCode={fp_code!}
                        tenureId={tenureId}
                        isFirst={i === 0}
                      />
                    ))
                  )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </>
  )
}

interface AreaSectionProps {
  meta: { key: string; label: string }
  slug: FetchableAreaSlug
  rows: ExceptionType[]
  fpCode: string
  tenureId?: number
  isFirst: boolean
}

const AreaSection: React.FC<AreaSectionProps> = ({ meta, slug, rows, fpCode, tenureId, isFirst }) => (
  <Box
    data-testid={`area-section-${meta.key}`}
    sx={{ mt: isFirst ? 0 : 3.5 }}
  >
    {/* Clear, list-style group heading: icon + name + count over a strong
        divider. Always shown - including a single-area view - so the domain is
        always labelled. */}
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.25}
      sx={{ pb: 1, mb: 1.5, borderBottom: 2, borderColor: 'divider' }}
    >
      <ExceptionDomainIcon domain={meta.key} variant="outlined" size={22} applyColor />
      <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
        {meta.label}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        - {rows.length} {plural(rows.length, 'report')}
      </Typography>
    </Stack>
    <ReportRows rows={rows} fpCode={fpCode} tenureId={tenureId} slug={slug} />
  </Box>
)

interface ReportRowsProps {
  rows: ExceptionType[]
  fpCode: string
  tenureId?: number
  slug: FetchableAreaSlug
}

const ReportRows: React.FC<ReportRowsProps> = ({ rows, fpCode, tenureId, slug }) => (
  <Box>
    {rows.map((type, i) => {
      // Per-row deep-link slug derives from the row's data_domain (the section
      // slug is the safe fallback if a domain field is unexpected).
      const rowDomain = getDomainMeta(type.data_domain).key
      const rowSlug = (domainToSlug(rowDomain) as FetchableAreaSlug) ?? slug
      return (
        <ReportRow
          key={`${type.code}-${i}`}
          type={type}
          to={generateExceptionUrl(fpCode, rowSlug, type.code, tenureId)}
        />
      )
    })}
  </Box>
)

interface TypeEmptyProps {
  typeLabel: string
  onClearType: () => void
}

/**
 * Shown when the type strip has rows to count but the active type filter
 * excludes them all - keeps the strip visible (it lives above this) and
 * offers a one-click way back.
 */
const TypeEmpty: React.FC<TypeEmptyProps> = ({ typeLabel, onClearType }) => (
  <Box sx={{ py: 1 }}>
    <Typography variant="body2" color="text.secondary">
      No {typeLabel} reports in view.
    </Typography>
    <Button
      variant="text"
      size="small"
      onClick={onClearType}
      sx={{ px: 0, mt: 0.5, textTransform: 'none' }}
    >
      Clear type filter
    </Button>
  </Box>
)

interface EmptyResultsProps {
  isAll: boolean
  areaLabel: string
  searchTerm: string
  onClearSearch: () => void
  onSearchAllAreas: () => void
}

/**
 * Filter-aware empty state, left-aligned inside the Results paper. Reflects the
 * active area + search so it reads as useful rather than generic.
 */
const EmptyResults: React.FC<EmptyResultsProps> = ({
  isAll,
  areaLabel,
  searchTerm,
  onClearSearch,
  onSearchAllAreas,
}) => {
  const hasSearch = searchTerm.trim().length > 0
  const hasArea = !isAll

  // No filters active and nothing configured - plain, no actions.
  if (!hasSearch && !hasArea) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
        No exception reports are configured for this franchise partner.
      </Typography>
    )
  }

  const body = hasSearch
    ? hasArea
      ? `No ${areaLabel} reports match "${searchTerm}".`
      : `No reports match "${searchTerm}".`
    : `No ${areaLabel} reports are configured for this franchise partner.`

  return (
    <Box sx={{ py: 1 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
        No reports found
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {body}
      </Typography>
      <Stack direction="row" spacing={2} sx={{ mt: 0.75 }}>
        {hasSearch && (
          <Button
            variant="text"
            size="small"
            onClick={onClearSearch}
            sx={{ px: 0, textTransform: 'none' }}
          >
            Clear search
          </Button>
        )}
        {hasArea && (
          <Button
            variant="text"
            size="small"
            onClick={onSearchAllAreas}
            sx={{ px: 0, textTransform: 'none' }}
          >
            Search all areas
          </Button>
        )}
      </Stack>
    </Box>
  )
}

export default ExceptionsHub
